import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LifecycleStateBadge, LifecyclePipeline, LIFECYCLE_STATE_LABELS } from "./LifecycleStateBadge";
import type { ContractLifecycleState } from "./LifecycleStateBadge";
import { SendExternalDialog } from "./SendExternalDialog";
import { LogCounterDialog } from "./LogCounterDialog";
import {
  Lock,
  PenLine,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Persona } from "@/lib/orgContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { handleApiError } from "@/lib/handleApiError";
import { LIFECYCLE_TRANSITION_ACTIONS } from "@/lib/lifecycleTransitions";

interface LifecycleActionsPanelProps {
  contractId: number;
  lifecycleState: ContractLifecycleState | string;
  isAdmin: boolean;
  personas: Persona[];
  isReadOnly?: boolean;
  onStateChanged: () => void;
  className?: string;
}

// TRANSITION_ACTIONS lives in `lib/lifecycleTransitions.ts` and is shared with
// LifecycleActionsMenu so the two surfaces never drift.
const TRANSITION_ACTIONS = LIFECYCLE_TRANSITION_ACTIONS;

export function LifecycleActionsPanel({
  contractId,
  lifecycleState,
  isAdmin,
  personas,
  isReadOnly,
  onStateChanged,
  className,
}: LifecycleActionsPanelProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [pendingTransition, setPendingTransition] = useState<ContractLifecycleState | null>(null);
  const [sendExternalOpen, setSendExternalOpen] = useState(false);
  const [logCounterOpen, setLogCounterOpen] = useState(false);

  const normalized = (lifecycleState ?? "draft") as ContractLifecycleState;
  const isLegal = personas.includes("Legal");
  const actions = TRANSITION_ACTIONS[normalized] ?? [];

  const handleTransition = async (toState: ContractLifecycleState) => {
    setPendingTransition(toState);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/lifecycle`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ toState }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw { status: res.status, data: body };
      }
      toast({
        title: "Status updated",
        description: `Contract moved to "${LIFECYCLE_STATE_LABELS[toState as ContractLifecycleState] ?? toState}".`,
      });
      onStateChanged();
    } catch (err) {
      handleApiError(toast, err, { fallbackTitle: "Could not update status" });
    } finally {
      setPendingTransition(null);
    }
  };

  if (!isAdmin || isReadOnly) {
    return (
      <div className={cn("space-y-3", className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-muted-foreground">Contract Phase</span>
          <LifecycleStateBadge state={normalized} />
        </div>
        <LifecyclePipeline currentState={normalized} />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Contract Phase</span>
        <LifecycleStateBadge state={normalized} />
      </div>

      <LifecyclePipeline currentState={normalized} />

      {normalized === "out_for_negotiation" && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-700 text-sm">
            Counterparty holds the current package. Log their counter here when they reply. To change
            our offer, use Revise Terms on Terms &amp; Compensation, then send another revision.
          </AlertDescription>
        </Alert>
      )}

      {normalized === "locked" && (
        <Alert className="border-red-200 bg-red-50">
          <Lock className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-700 text-sm">
            Document is locked. All clauses are read-only. Only Legal can unlock.
          </AlertDescription>
        </Alert>
      )}

      {actions.length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Available Actions</p>
            <div className="flex flex-col gap-2">
              {actions.map((action) => {
                const disabled =
                  !!pendingTransition ||
                  (action.requiresLegal && !isLegal);

                const isSendExternal = action.toState === "out_for_negotiation" && normalized !== "out_for_negotiation";
                const isLogCounter = action.toState === "counter_received";

                return (
                  <div key={action.toState} className="flex items-start gap-3">
                    <Button
                      variant={action.variant}
                      size="sm"
                      disabled={disabled}
                      className={cn("flex-shrink-0", action.primary && action.variant === "default" && "bg-[#0B1834] hover:bg-[#0B1834]/90")}
                      onClick={() => {
                        if (isSendExternal) {
                          setSendExternalOpen(true);
                        } else if (isLogCounter) {
                          setLogCounterOpen(true);
                        } else {
                          void handleTransition(action.toState);
                        }
                      }}
                    >
                      <action.icon className="h-3.5 w-3.5 mr-1.5" />
                      {pendingTransition === action.toState ? "Updating…" : action.label}
                    </Button>
                    <div>
                      <p className="text-xs text-muted-foreground">{action.description}</p>
                      {action.requiresLegal && !isLegal && (
                        <p className="text-xs text-destructive mt-0.5">Requires Legal persona</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {normalized === "signed" && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-md px-3 py-2 border border-emerald-200">
          <PenLine className="h-4 w-4" />
          <span>Contract is fully executed and signed.</span>
        </div>
      )}

      <SendExternalDialog
        open={sendExternalOpen}
        onOpenChange={setSendExternalOpen}
        contractId={contractId}
        onSuccess={() => {
          setSendExternalOpen(false);
          onStateChanged();
        }}
      />

      <LogCounterDialog
        open={logCounterOpen}
        onOpenChange={setLogCounterOpen}
        contractId={contractId}
        onSuccess={onStateChanged}
      />
    </div>
  );
}
